import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CButton, CCard, CCardBody } from "@coreui/react";
import { FilterRunControls } from "@src/components/FilterRunControls/FilterRunControls";
import { FromJobModal } from "@src/components/FromJobModal/FromJobModal";
import { PaginationControls } from "@src/components/PaginationControls";
import { useEntityFilters } from "@src/hooks/useEntityFilters";
import { useBacktests, useExecutions } from "@src/hooks/redux";
import { useApiPagination } from "@src/hooks/usePagination";
import { getEntityConfig } from "@src/services/config/entityConfigs";
import type * as T from "@src/types";
import { convertRunFiltersToQuery } from "@src/utils/api/runFilters";
import { PAGINATION_STALLED_MESSAGE } from "@src/utils/constants";
import { RunListTable } from "./RunListTable";

interface Props {
  type: "backtest" | "execution";
}

export const RunsListPage: React.FC<Props> = ({ type }) => {
  const navigate = useNavigate();
  const [showFromJobModal, setShowFromJobModal] = useState(false);

  // Get data from Redux based on type
  const { backtests: allBacktests, setBacktests } = useBacktests();
  const { executions: allExecutions, setExecutions } = useExecutions();

  // Use Redux for both types
  const allRuns: T.ApiRun[] = type === "backtest" ? allBacktests : allExecutions;

  // Get entity config to use its pageSize
  const runType = type === "backtest" ? "backtests" : "executions";
  const entityKey = type === "backtest" ? "backtests" : "executions";
  const defaultPageSize = getEntityConfig(runType).pageSize;

  // Use new unified filter/pagination hook
  const { filters, pagination, applyFilters, clearFilters, setPerPage, nextPage, prevPage } =
    useEntityFilters(entityKey);

  // Typed wrapper for setItems to avoid runtime casts
  const setItems = useCallback(
    (items: (T.TDT.ApiExecution | T.TDT.ApiBacktest)[]) => {
      if (type === "execution") {
        setExecutions(items as T.TDT.ApiExecution[]);
      } else {
        setBacktests(items as T.TDT.ApiBacktest[]);
      }
    },
    [type, setExecutions, setBacktests],
  );

  // Unified API hook: handles initial fetch + pagination with single loading state
  // ALL filters are sent to API - no client-side filtering needed
  const { loading, fetchNextPage, hasMore, stalled } = useApiPagination({
    entityName: runType,
    currentItems: allRuns as (T.TDT.ApiExecution | T.TDT.ApiBacktest)[],
    setItems,
    queryFilter: convertRunFiltersToQuery(filters),
  });

  // Calculate current page items (allRuns already filtered by API)
  const pageRuns = React.useMemo(() => {
    const start = pagination.page * pagination.perPage;
    const end = start + pagination.perPage;
    return allRuns.slice(start, end);
  }, [allRuns, pagination.page, pagination.perPage]);

  const handleNewRun = useCallback(() => {
    const path = type === "backtest" ? "/backtest/new" : "/execution/new";
    navigate(path);
  }, [type, navigate]);

  return (
    <CCard>
      <CCardBody>
        <FilterRunControls
          appliedFilters={filters}
          onApplyFilters={applyFilters}
          onClearFilters={clearFilters}
          disabled={loading}
        />

        <PaginationControls
          itemLabel={type === "backtest" ? "backtests" : "executions"}
          currentPage={pagination.page}
          totalItems={allRuns.length}
          perPage={pagination.perPage}
          onPerPageChange={setPerPage}
          onPrevPage={prevPage}
          onNextPage={nextPage}
          onNextRequest={fetchNextPage}
          isLoading={loading}
          hasMore={hasMore}
          notice={stalled ? PAGINATION_STALLED_MESSAGE : undefined}
          defaultPageSize={defaultPageSize}
          disabled={loading}
          addon={
            <div className="d-flex gap-2">
              <CButton
                color="secondary"
                size="sm"
                onClick={() => setShowFromJobModal(true)}
                disabled={loading}
              >
                From Job
              </CButton>
              <CButton color="success" size="sm" onClick={handleNewRun} disabled={loading}>
                {type === "backtest" ? "+ New Backtest" : "+ New Execution"}
              </CButton>
            </div>
          }
        />

        <RunListTable type={type} runs={pageRuns} loading={loading} />

        <FromJobModal visible={showFromJobModal} onClose={() => setShowFromJobModal(false)} />
      </CCardBody>
    </CCard>
  );
};
